import { useEffect, useState, type FormEvent } from "react";
import { CircleOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { CopyIdButton } from "@/components/admin/copy-id-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type DeviceTarget = {
  activationId: string;
  licenseId: string;
  computerName: string;
  location?: string | null;
};

export function DeactivateActivationDialog({
  open,
  onOpenChange,
  target,
  busy,
  error,
  onDeactivate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DeviceTarget;
  busy: boolean;
  error?: string | null;
  onDeactivate: (activationId: string, reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setReason("");
    setValidation(null);
  }, [open]);

  function close(next: boolean) {
    if (!next && busy) return;
    if (!next) {
      setReason("");
      setValidation(null);
    }
    onOpenChange(next);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setValidation(
        t(
          "dialogs.deactivateActivationReasonRequired",
          "A reason is required to deactivate this Activation.",
        ),
      );
      return;
    }
    setValidation(null);
    onDeactivate(target.activationId, normalizedReason);
  }

  const inputError = validation || error;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" showClose={!busy}>
        <form onSubmit={submit}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <CircleOff className="size-5" aria-hidden="true" />
              <DialogTitle className="text-base font-semibold">
                {t("dialogs.deactivateActivationTitle", "Deactivate Activation")}
              </DialogTitle>
            </div>
            <DialogDescription className="mt-2 text-xs leading-5 text-muted">
              {t(
                "dialogs.deactivateActivationConfirm",
                "Close this Activation and release one License slot. An active Product Key can activate again.",
              )}
            </DialogDescription>
          </DialogHeader>

          <dl className="mt-4 space-y-2 rounded-lg border border-line bg-panel px-3 py-2.5 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">{t("devices.deviceCode", "Device code")}</dt>
              <dd className="flex min-w-0 items-center gap-1 font-mono text-xs text-ink">
                <span className="truncate">{target.activationId}</span>
                <CopyIdButton value={target.activationId} label={t("devices.deviceCode", "Device code")} />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">{t("common.license", "License")}</dt>
              <dd className="flex min-w-0 items-center gap-1 font-mono text-xs text-ink">
                <span className="truncate">{target.licenseId}</span>
                <CopyIdButton value={target.licenseId} label={t("common.license", "License")} />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">{t("devices.computerName", "Computer")}</dt>
              <dd className="truncate text-right text-ink">{target.computerName}</dd>
            </div>
            {target.location && (
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted">{t("devices.location", "Location")}</dt>
                <dd className="truncate text-right text-ink">{target.location}</dd>
              </div>
            )}
          </dl>

          <p className="mt-3 text-xs leading-5 text-muted">
            {t(
              "dialogs.deactivateActivationOfflineConsequence",
              "This is not an immediate remote kill. A signed offline token may continue to work until its signed token or offline window expires.",
            )}
          </p>

          <div className="mt-4 space-y-2">
            <Label htmlFor="deactivate-activation-reason" className="text-xs">
              {t("dialogs.reason", "Reason")} <span className="text-danger">*</span>
            </Label>
            <Input
              id="deactivate-activation-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setValidation(null);
              }}
              aria-invalid={Boolean(inputError)}
              aria-describedby={inputError ? "deactivate-activation-reason-error" : undefined}
              placeholder={t(
                "dialogs.deactivateActivationReasonPlaceholder",
                "e.g. device was replaced",
              )}
              autoFocus
              disabled={busy}
            />
          </div>

          {inputError && (
            <div id="deactivate-activation-reason-error" className="mt-3">
              <InlineAlert
                title={t("search.details.actionBlocked", "Action blocked")}
                message={inputError}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => close(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" size="sm" disabled={!reason.trim() || busy}>
              {busy ? (
                <BusyLabel>{t("dialogs.deactivating", "Deactivating")}</BusyLabel>
              ) : (
                t("dialogs.confirmDeactivate", "Deactivate")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
